import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';

import { Menu } from '@headlessui/react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';

import { useGetTeam } from '@entities/myTeam/lib/hooks';
import { IEmployee } from '@entities/myTeam/model/types';
import { changeTariff } from '@entities/tariff/lib';
import { Icon, Popup, UiText } from '@shared/ui';
import { useMyTeamContext } from '@widgets/MyTeam/model/context';

import cls from './EmployeeActions.module.css';
import { useEmployeeActions } from '../../lib/hooks/useEmployeeActions';

interface EmployeeActionsProps {
    isCanUseTasks: boolean;
    employeeId: IEmployee['id'];
    currentEmployeeId: IEmployee['id'];
    isCanManageEmployees: boolean;
    isOwner: boolean;
}

export const EmployeeActions = ({
    isCanUseTasks,
    employeeId,
    currentEmployeeId,
    isCanManageEmployees,
    isOwner,
}: EmployeeActionsProps) => {
    const { t, isShowPopup, onHidePopup, onDelete, onShowPopup } = useEmployeeActions({
        employeeId,
        currentEmployeeId,
    });
    const { onChangeCurrentModal, onChangeCurrentClickedId } = useMyTeamContext();

    const { data } = useGetTeam();

    const { isCanEditEmployeeAccessesByTariff, isOwner: isCompanyOwner } = data || {};

    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
    const [open, setOpen] = useState(false);

    const menuRef = useRef<HTMLDivElement | null>(null);

    const handleOpen = useCallback(() => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setMenuPos({
                top: rect.bottom + 6,
                left: rect.right - 192,
            });
            setOpen(true);
        }
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
        setMenuPos(null);
    }, []);

    const canShowDelete = useMemo(() => {
        // if (currentEmployeeId === employeeId) return true;
        if (isOwner) return false;
        if (isCanManageEmployees) return true;
        // if (!isOwner) return true;
        return !isOwner && isCanManageEmployees;
    }, [isOwner, isCanManageEmployees]);

    const canShowAccessSettings = useMemo(() => {
        if (isOwner) return false;
        return !isOwner && isCanManageEmployees;
    }, [isOwner, isCanManageEmployees]);

    useEffect(() => {
        if (!open) return;
        function handleClick(event: MouseEvent) {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                handleClose();
            }
        }
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open, handleClose]);

    useEffect(() => {
        if (!open) return;
        function handleKey(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                handleClose();
            }
        }
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [open, handleClose]);

    return (
        <>
            <Popup
                type="delete"
                isOpen={isShowPopup}
                onHidePopup={onHidePopup}
                btnActionCb={onDelete}
                contentText={t('EmployerActions.Popup.contentText')}
            />
            <Menu>
                <Menu.Button
                    ref={buttonRef}
                    onClick={(e) => {
                        e.stopPropagation();
                        open ? handleClose() : handleOpen();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            open ? handleClose() : handleOpen();
                        }
                    }}
                    className={cls.EmployerActions}
                >
                    <Icon iconName="filesDropdownDots" />
                </Menu.Button>
                {open &&
                    menuPos &&
                    createPortal(
                        <Menu.Items
                            ref={menuRef}
                            className={cls.menuItems}
                            style={{
                                position: 'fixed',
                                top: menuPos.top,
                                left: menuPos.left,
                                zIndex: 1000,
                            }}
                        >
                            {canShowAccessSettings && (
                                <Menu.Item>
                                    {(active) => (
                                        <span
                                            className={classNames(cls.item, {
                                                [cls.active]: active,
                                            })}
                                            onClick={async (event) => {
                                                if (isCanEditEmployeeAccessesByTariff) {
                                                    event.stopPropagation();
                                                    onChangeCurrentClickedId(employeeId);
                                                    onChangeCurrentModal('accessSettings');
                                                    handleClose();
                                                    return;
                                                }
                                                await changeTariff(isCompanyOwner);
                                                handleClose();
                                            }}
                                        >
                                            <Icon iconName="employerSetting" />
                                            <UiText tag="span" variant="mediumPlain">
                                                {t('EmployerActions.return.Menu.Item.settings')}
                                            </UiText>
                                        </span>
                                    )}
                                </Menu.Item>
                            )}
                            <Menu.Item>
                                {(active) => (
                                    <span
                                        className={classNames(cls.item, {
                                            [cls.active]: active,
                                        })}
                                        onClick={async (event) => {
                                            event.stopPropagation();
                                            if (isCanUseTasks) {
                                                onChangeCurrentModal('assignTaskToEmployee');
                                                onChangeCurrentClickedId(employeeId);
                                                handleClose();
                                                return;
                                            }
                                            await changeTariff(isCompanyOwner);
                                            handleClose();
                                        }}
                                    >
                                        <Icon iconName="addTask" />
                                        <UiText tag="span" variant="mediumPlain">
                                            {t('EmployerActions.return.Menu.Item.addTask')}
                                        </UiText>
                                    </span>
                                )}
                            </Menu.Item>

                            {canShowDelete && (
                                <Menu.Item>
                                    {(active) => (
                                        <span
                                            className={classNames(cls.item, cls.delete, {
                                                [cls.active]: active,
                                            })}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onShowPopup();
                                                onChangeCurrentClickedId(employeeId);
                                                handleClose();
                                            }}
                                        >
                                            <Icon iconName="filesDelete" fill="#ec3030" />
                                            <UiText tag="span" variant="mediumPlain">
                                                {t('EmployerActions.return.Menu.Item.delete')}
                                            </UiText>
                                        </span>
                                    )}
                                </Menu.Item>
                            )}
                        </Menu.Items>,
                        document.body
                    )}
            </Menu>
        </>
    );
};
